import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Button } from '@/components/ui/Button'
import { FormField } from '@/components/forms/FormField'
import { MultiSelectChips } from '@/components/forms/MultiSelectChips'
import {
  supplierService,
  supplierFormatService,
  type GoodsCategory,
  type PaymentTerm,
  type SupplierInput,
  type SupplierStatus,
} from '@/services/suppliers'
import type { CatalogItem } from '@/services/catalog'

const inputClass =
  'w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2.5 py-1.5 text-sm outline-none focus:border-[var(--color-accent)]'

type FormState = {
  code: string
  name: string
  category_ids: string[]
  status: SupplierStatus
  address: string
  contact_person: string
  phone: string
  email: string
  tax_code: string
  default_vat_rate: string
  payment_term_id: string
  lead_time_days: string
  note: string
}

const emptyForm = (): FormState => ({
  code: '',
  name: '',
  category_ids: [],
  status: 'ACTIVE',
  address: '',
  contact_person: '',
  phone: '',
  email: '',
  tax_code: '',
  default_vat_rate: '8',
  payment_term_id: '',
  lead_time_days: '7',
  note: '',
})

function toCatalogOptions(cats: GoodsCategory[]): CatalogItem[] {
  return cats.map((c) => ({
    id: c.id,
    code: c.code,
    name: c.name,
    isActive: c.isActive,
    note: '',
    sopFiles: [],
  }))
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="border border-[var(--color-border)] bg-white">
      <div className="border-b border-[var(--color-border)] bg-slate-50 px-3 py-2 text-xs font-semibold tracking-wide text-slate-600 uppercase">
        {title}
      </div>
      <div className="space-y-3 p-3 sm:p-4">{children}</div>
    </section>
  )
}

export function SupplierFormPage() {
  const { supplierId } = useParams()
  const isEdit = Boolean(supplierId)
  const navigate = useNavigate()
  const [form, setForm] = useState<FormState>(emptyForm)
  const [categories, setCategories] = useState<GoodsCategory[]>(() =>
    supplierService.listCategories(),
  )
  const [terms, setTerms] = useState<PaymentTerm[]>(() =>
    supplierService.listPaymentTerms(),
  )
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const categoryOptions = useMemo(() => {
    const list = [...categories]
    for (const id of form.category_ids) {
      if (!list.some((c) => c.id === id)) {
        const cat = supplierService.getCategory(id)
        list.push({
          id,
          code: cat?.code || id.toUpperCase(),
          name: cat?.name || id,
          isActive: true,
        })
      }
    }
    return toCatalogOptions(list)
  }, [categories, form.category_ids])

  useEffect(() => {
    setCategories(supplierService.listCategories())
    void supplierFormatService.fetchFormats().then(() => {
      setCategories(supplierService.listCategories())
    })
    const pts = supplierService.listPaymentTerms()
    setTerms(pts)
    if (!isEdit) {
      setForm((f) => ({
        ...f,
        payment_term_id: pts.find((t) => t.code === 'NET30')?.id ?? pts[0]?.id ?? '',
      }))
    }
  }, [isEdit])

  useEffect(() => {
    if (!supplierId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    supplierService.getById(supplierId).then((s) => {
      if (cancelled) return
      if (!s) {
        setError('Không tìm thấy nhà cung cấp.')
        setLoading(false)
        return
      }
      setForm({
        code: s.code,
        name: s.name,
        category_ids: [...s.category_ids],
        status: s.status,
        address: s.address ?? '',
        contact_person: s.contact_person ?? '',
        phone: s.phone ?? '',
        email: s.email ?? '',
        tax_code: s.tax_code ?? '',
        default_vat_rate: String(s.default_vat_rate),
        payment_term_id: s.payment_term_id,
        lead_time_days: String(s.lead_time_days),
        note: s.note ?? '',
      })
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [supplierId])

  function buildInput(): SupplierInput {
    return {
      code: form.code,
      name: form.name,
      category_ids: form.category_ids,
      status: form.status,
      address: form.address,
      contact_person: form.contact_person,
      phone: form.phone,
      email: form.email,
      tax_code: form.tax_code,
      default_vat_rate: Number(form.default_vat_rate),
      payment_term_id: form.payment_term_id,
      lead_time_days: Number(form.lead_time_days),
      note: form.note,
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSaving(true)
    try {
      const input = buildInput()
      if (isEdit && supplierId) {
        const updated = await supplierService.update(supplierId, input)
        navigate(`/purchasing/suppliers/${updated.id}`)
      } else {
        const created = await supplierService.create(input)
        navigate(`/purchasing/suppliers/${created.id}`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể lưu NCC')
    } finally {
      setSaving(false)
    }
  }

  function cancel() {
    if (isEdit && supplierId) navigate(`/purchasing/suppliers/${supplierId}`)
    else navigate('/purchasing/suppliers')
  }

  if (loading) {
    return <p className="text-sm text-slate-500">Đang tải...</p>
  }

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          { label: 'Nhà cung cấp', to: '/purchasing/suppliers' },
          { label: isEdit ? 'Sửa' : 'Nhà cung cấp mới' },
        ]}
      />
      <PageHeader
        title={isEdit ? `Sửa ${form.code || 'NCC'}` : 'Nhà cung cấp mới'}
        subtitle="Form master — lưu Postgres qua API"
      />

      <form onSubmit={(e) => void handleSubmit(e)} className="max-w-4xl space-y-3">
        {error ? (
          <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <Section title="Thông tin cơ bản">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Mã NCC" required>
              <input
                required
                className={inputClass}
                value={form.code}
                onChange={(e) =>
                  setForm({ ...form, code: e.target.value.toUpperCase() })
                }
                placeholder="NCC-GO-001"
              />
            </FormField>
            <FormField label="Tên NCC" required>
              <input
                required
                className={inputClass}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Thành Công"
              />
            </FormField>
            <FormField label="Nhóm hàng" required>
              <MultiSelectChips
                options={categoryOptions}
                value={form.category_ids}
                onChange={(ids) => setForm({ ...form, category_ids: ids })}
                placeholder="Chọn nhóm hàng..."
              />
            </FormField>
            <FormField label="Trạng thái" required>
              <select
                className={inputClass}
                value={form.status}
                onChange={(e) =>
                  setForm({
                    ...form,
                    status: e.target.value as SupplierStatus,
                  })
                }
              >
                <option value="ACTIVE">Đang hoạt động</option>
                <option value="INACTIVE">Ngừng hoạt động</option>
              </select>
            </FormField>
          </div>
        </Section>

        <Section title="Thông tin liên hệ">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <FormField label="Địa chỉ">
                <textarea
                  rows={2}
                  className={inputClass}
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Một field địa chỉ duy nhất — ví dụ: Bình Dương"
                />
              </FormField>
            </div>
            <FormField label="Người liên hệ">
              <input
                className={inputClass}
                value={form.contact_person}
                onChange={(e) =>
                  setForm({ ...form, contact_person: e.target.value })
                }
              />
            </FormField>
            <FormField label="Điện thoại">
              <input
                className={inputClass}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </FormField>
            <FormField label="Email">
              <input
                type="email"
                className={inputClass}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </FormField>
          </div>
        </Section>

        <Section title="Điều kiện mua hàng">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Mã số thuế">
              <input
                className={inputClass}
                value={form.tax_code}
                onChange={(e) => setForm({ ...form, tax_code: e.target.value })}
              />
            </FormField>
            <FormField label="VAT mặc định (%)" required>
              <input
                required
                type="number"
                min={0}
                max={100}
                step={0.01}
                className={inputClass}
                value={form.default_vat_rate}
                onChange={(e) =>
                  setForm({ ...form, default_vat_rate: e.target.value })
                }
              />
            </FormField>
            <FormField label="Điều khoản thanh toán" required>
              <select
                required
                className={inputClass}
                value={form.payment_term_id}
                onChange={(e) =>
                  setForm({ ...form, payment_term_id: e.target.value })
                }
              >
                <option value="" disabled>
                  Chọn điều khoản...
                </option>
                {terms.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Lead Time mặc định (ngày)" required>
              <input
                required
                type="number"
                min={0}
                step={1}
                className={inputClass}
                value={form.lead_time_days}
                onChange={(e) =>
                  setForm({ ...form, lead_time_days: e.target.value })
                }
              />
            </FormField>
          </div>
        </Section>

        <Section title="Ghi chú">
          <FormField label="Ghi chú">
            <textarea
              rows={3}
              className={inputClass}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Ghi chú nội bộ..."
            />
          </FormField>
        </Section>

        <div className="flex justify-end gap-2 border border-[var(--color-border)] bg-slate-50 px-3 py-2.5">
          <Button type="button" variant="secondary" onClick={cancel} disabled={saving}>
            Hủy
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Đang lưu...' : isEdit ? 'Lưu' : 'Lưu NCC'}
          </Button>
        </div>
      </form>
    </div>
  )
}
